import { useMemo, useRef } from "react";
import {
  getGraduationYearValue,
  isGraduationYearQuestion,
  isPhoneQuestion,
} from "../../../apply/profileQuestions";
import "./applicationDetails.css";

const QUESTION_PRIORITY = {
  long_answer: 0,
  short_answer: 1,
};

const getQuestionPriority = (type) =>
  QUESTION_PRIORITY[type] ?? Number.MAX_SAFE_INTEGER;

const formatAnswer = (answer, type) => {
  if (answer === undefined || answer === null || answer === "") {
    return "Not provided";
  }

  if (type === "boolean") {
    if (answer === "yes") return "Yes";
    if (answer === "no") return "No";
  }

  if (type === "multiselect" || type === "list") {
    return String(answer)
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .join(", ");
  }

  return String(answer);
};

const ApplicationDetails = ({ application, questions, loading }) => {
  const detailsRef = useRef(null);
  const toggleAll = (open) => {
    detailsRef.current.querySelectorAll("details").forEach((section) => {
      section.open = open;
    });
  };
  const savedSections = application?.sections || [];
  const graduationYear = getGraduationYearValue(
    savedSections,
    application?.graduation_year
  );
  const phoneNumber = application?.phone_number || savedSections
    .flatMap((section) => section.answers || [])
    .find((answer) => isPhoneQuestion(answer.question_id) && answer.answer)?.answer;
  const profileFields = [
    ["First Name", application?.first],
    ["Last Name", application?.last],
    ["Email", application?.email],
    ["Verified School Email", application?.school_email || "Not verified"],
    ["University", application?.school],
    ["Phone Number", phoneNumber],
    ["Graduation Year", graduationYear],
    ["LinkedIn Profile", application?.linkedin_profile_url],
    ["GitHub Profile", application?.github_profile_url],
    ["Team Code", application?.teamcode],
  ];

  const sectionDetails = useMemo(() => {
    if (!application?.sections) {
      return [];
    }

    const sections = [
      ...(questions || []),
      ...application.sections
        .filter((saved) => !(questions || []).some(
          (section) => section.section_name === saved.section_name
        ))
        .map((saved) => ({ section_name: saved.section_name, questions: [] })),
    ];
    const questionSections = sections.map((section, sectionIndex) => {
      const appSection = application.sections.find(
        (saved) => saved.section_name === section.section_name
      );

      if (!appSection) {
        return null;
      }

      const answersById = new Map(
        (appSection.answers || []).map((answer) => [answer.question_id, answer.answer])
      );

      const knownQuestions = section.questions.map((question, questionIndex) => {
        const savedAnswer = answersById.get(question.question_id);
        const profileAnswer = isGraduationYearQuestion(question.question_id)
          ? graduationYear
          : isPhoneQuestion(question.question_id) ? phoneNumber : undefined;

        return {
          ...question,
          questionIndex,
          answer: savedAnswer === "" || savedAnswer == null
            ? profileAnswer
            : savedAnswer,
        };
      });

      const unknownQuestions = (appSection.answers || [])
        .filter(
          (answer) =>
            !section.questions.some(
              (question) => question.question_id === answer.question_id
            )
        )
        .map((answer, index) => ({
          question_id: answer.question_id,
          question_text: answer.question_id,
          question_type: "short_answer",
          questionIndex: section.questions.length + index,
          answer: answer.answer,
        }));

      const mergedQuestions = [...knownQuestions, ...unknownQuestions].sort(
        (a, b) => {
          const priorityDelta =
            getQuestionPriority(a.question_type) -
            getQuestionPriority(b.question_type);

          if (priorityDelta !== 0) {
            return priorityDelta;
          }

          return a.questionIndex - b.questionIndex;
        }
      );

      return {
        section_name: section.section_name,
        sectionIndex,
        hasShortAnswers: mergedQuestions.some(
          (question) => question.question_type === "short_answer"
        ),
        hasLongAnswers: mergedQuestions.some(
          (question) => question.question_type === "long_answer"
        ),
        questions: mergedQuestions,
      };
    });

    return questionSections
      .filter(Boolean)
      .sort((a, b) => {
        const aPriority = a.hasLongAnswers ? 0 : a.hasShortAnswers ? 1 : 2;
        const bPriority = b.hasLongAnswers ? 0 : b.hasShortAnswers ? 1 : 2;

        if (aPriority === bPriority) {
          return a.sectionIndex - b.sectionIndex;
        }

        return aPriority - bPriority;
      });
  }, [application, questions, graduationYear, phoneNumber]);

  if (loading) {
    return <p className="app-details-message">Loading application details...</p>;
  }

  if (!application?.email) {
    return <p className="app-details-message">Application details unavailable.</p>;
  }

  return (
    <div className="app-details-root" ref={detailsRef}>
      <div className="app-details-controls" role="group" aria-label="Application sections">
        <button type="button" onClick={() => toggleAll(true)}>Expand all</button>
        <button type="button" onClick={() => toggleAll(false)}>Collapse all</button>
      </div>
      <details className="app-details-section" open>
        <summary className="app-details-section-title">Applicant Information</summary>
        <div className="app-details-grid">
          {profileFields.map(([label, value]) => (
            <article key={label} className="app-details-item">
              <p className="app-details-question">{label}</p>
              <p className="app-details-answer">{formatAnswer(value)}</p>
            </article>
          ))}
        </div>
      </details>
      {sectionDetails.map((section, index) => (
        <details
          key={section.section_name}
          className="app-details-section"
          open={index === 0}
        >
          <summary className="app-details-section-title">
            {section.section_name}
          </summary>
          <div className="app-details-grid">
            {section.questions.map((question) => (
              <article
                key={question.question_id}
                className={`app-details-item app-details-item-${question.question_type}`}
              >
                <p className="app-details-question">{question.question_text}</p>
                <p className="app-details-answer">
                  {formatAnswer(question.answer, question.question_type)}
                </p>
              </article>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
};

export default ApplicationDetails;
